'use client';

import React, { PropsWithChildren, useEffect } from 'react';
import RollbarProvider from '@/providers/RollbarProvider';
import StoreProvider from '@/providers/StoreProvider';
import ThemeProvider from '@/providers/ThemeProvider';
import ProgressProvider from '@/providers/ProgressProvider';
import NotificationBannerProvider from '@/providers/NotificationBannerProvider';
import { FeatureProvider } from '@/providers/FeatureProvider/FeatureProvider';
import { notifications } from '@mantine/notifications';
import { usePathname, useRouter } from 'next/navigation';
import { cleanUserLocalState } from '@/utils/user';
import { getCookie, setCookie } from 'cookies-next';
import { ORGANIZATION } from '@/utils/storageKeys';
import { StoreInterface } from '@/stores/store';

type Props = {
  initialStoreData?: Partial<Omit<StoreInterface, 'updateData'>>;
  toastMessage?: string;
  shouldLogoutUser?: boolean;
};

const Providers: React.FC<PropsWithChildren<Props>> = ({
  children,
  toastMessage,
  shouldLogoutUser,
  initialStoreData = {},
}) => {
  const pathname = usePathname();
  const router = useRouter();

  const user = initialStoreData?.user || null;

  useEffect(() => {
    if (
      typeof window !== 'undefined' &&
      window.location.hostname.includes('assistente.appmasters')
    ) {
      router.push('/novo-endereco');
    }
  }, [router]);

  useEffect(() => {
    if (toastMessage && pathname?.includes('entrar')) {
      notifications.show({
        title: 'Ocorreu um erro',
        message: toastMessage,
        color: 'red',
      });
    }
  }, [toastMessage, pathname]);

  useEffect(() => {
    if (shouldLogoutUser) {
      cleanUserLocalState();
    }
  }, [shouldLogoutUser]);

  useEffect(() => {
    const preventZoom = (e: TouchEvent) => {
      // Previne o zoom se houver mais de um toque (gesto de "pinça")
      if (e.touches.length > 1) {
        e.preventDefault();
      }
    };

    const preventGesture = (e: Event) => {
      e.preventDefault();
    };

    // Bloqueia gestos de zoom no iOS
    document.addEventListener('gesturestart', preventGesture);
    document.addEventListener('gesturechange', preventGesture);
    document.addEventListener('gestureend', preventGesture);

    // Bloqueia o zoom por gesto de pinça, mas permite o scroll normal
    document.addEventListener('touchmove', preventZoom, { passive: false });

    return () => {
      document.removeEventListener('gesturestart', preventGesture);
      document.removeEventListener('gesturechange', preventGesture);
      document.removeEventListener('gestureend', preventGesture);
      document.removeEventListener('touchmove', preventZoom);
    };
  }, []);

  useEffect(() => {
    const orgCookie = getCookie(ORGANIZATION);
    if (orgCookie) {
      return;
    }
    const orgUser = user?.organizations?.find((uOrg) => uOrg.organization.active);
    if (!orgUser) return;

    const maxAge = 1000 * 60 * 60 * 24 * 365;
    setCookie(
      ORGANIZATION,
      JSON.stringify({ id: orgUser.organization.id, name: orgUser.organization.name }),
      { maxAge },
    );
    const path = '/';
    fetch(`/api/revalidate?path=${encodeURIComponent(path)}&layout=true`).then(() =>
      window.location.reload(),
    );
  }, [user]);

  return (
    <RollbarProvider user={user}>
      <StoreProvider initialData={{ ...initialStoreData }}>
        <FeatureProvider>
          <ThemeProvider>
            <ProgressProvider>
              <NotificationBannerProvider>{children}</NotificationBannerProvider>
            </ProgressProvider>
          </ThemeProvider>
        </FeatureProvider>
      </StoreProvider>
    </RollbarProvider>
  );
};

export default Providers;
